/* ──────────────────────────────────────────────────────────
   RoadmapChart — interactive timeline visualization

   Renders the user's roadmap as a horizontal Chart.js scatter
   chart with a connecting line. Each milestone is a circular
   point sized responsively to the chart width. A custom HTML
   tooltip surfaces the milestone's full content: timeframe,
   description, and any per-milestone bullet details set in the
   admin dashboard.

   The component intentionally keeps Chart.js wired narrow:
     - one dataset (the milestones), one line + custom point styling
     - no x-axis grid lines, no legend, no tick labels (we render
       our own caption row beneath the chart for typographic control)
     - a custom external tooltip so we can match the brand styling
       (Inter/DM Sans title, DM Mono labels, dark glass + accent)

   Required props:
     milestones — array of { _id, label, description, percent,
                  status, timeframeLabel, tooltipDetails[] }
   Optional:
     accent     — accent hex (default #C9B188)
     ink        — ink hex (default #1a1916)

   The parent (Roadmap section in Sections.js) handles loading
   state and the "starter" preview note.
   ────────────────────────────────────────────────────────── */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

// Inject gold pulse keyframes once
if (typeof document !== "undefined" && !document.getElementById("roadmap-pulse-style")) {
  const style = document.createElement("style");
  style.id = "roadmap-pulse-style";
  style.textContent = `
    @keyframes goldPulse {
      0%   { transform: translate(-50%, -50%) scale(1);   opacity: 0.7; }
      60%  { transform: translate(-50%, -50%) scale(2.8); opacity: 0; }
      100% { transform: translate(-50%, -50%) scale(2.8); opacity: 0; }
    }
    @keyframes goldPulse2 {
      0%   { transform: translate(-50%, -50%) scale(1);   opacity: 0.4; }
      60%  { transform: translate(-50%, -50%) scale(4.2); opacity: 0; }
      100% { transform: translate(-50%, -50%) scale(4.2); opacity: 0; }
    }
  `;
  document.head.appendChild(style);
}

const C = {
  ink:    "#F5F5F5",
  mid:    "rgba(255,255,255,0.72)",
  soft:   "rgba(255,255,255,0.56)",
  muted:  "rgba(255,255,255,0.40)",
  faint:  "rgba(255,255,255,0.18)",
  border: "rgba(255,255,255,0.10)",
  bg:     "transparent",
  bgAlt:  "rgba(255,255,255,0.04)",
  accent: "#E5D3A3",
};

/* ─── Status helpers ───────────────────────────────────── */
function colorFor(milestone, accent, ink) {
  if (milestone.status === "completed" || milestone.percent >= 100) return ink;
  if (milestone.status === "in_progress") return accent;
  if (milestone.status === "blocked") return "#f87171";
  return C.faint; // not_started
}

function isFilled(milestone) {
  return milestone.status === "completed"
      || milestone.percent >= 100
      || milestone.status === "in_progress";
}

/* ─── External (HTML) tooltip handler ──────────────────── */
// Chart.js gives us tooltip state; we render our own DOM node so
// the tooltip can match the brand and contain rich content.
function externalTooltipHandler({ chart, tooltip }, milestones, tooltipElRef) {
  const el = tooltipElRef.current;
  if (!el) return;

  if (tooltip.opacity === 0) {
    el.style.opacity = 0;
    return;
  }

  const dp = tooltip.dataPoints?.[0];
  if (!dp) return;
  const m = milestones[dp.dataIndex];
  if (!m) return;

  // Build tooltip body
  const detailLines = (m.tooltipDetails || [])
    .map((d) => `<li style="margin:4px 0;color:${C.soft};font-size:12px;line-height:1.5;display:flex;gap:8px;align-items:flex-start;"><span style="color:${C.accent};flex-shrink:0;">▸</span><span>${escapeHtml(d)}</span></li>`)
    .join("");

  const statusLabel = ({
    completed:    "Completed",
    in_progress:  "In progress",
    blocked:      "Blocked",
    not_started:  "Upcoming",
  })[m.status] || m.status;

  el.innerHTML = `
    <div style="background:#1E1E1E;border:1px solid rgba(255,255,255,0.12);border-radius:16px;padding:18px 20px;min-width:240px;max-width:320px;box-shadow:0 20px 48px rgba(0,0,0,0.5);backdrop-filter:blur(20px);">
      ${m.timeframeLabel ? `<div style="font-family:'DM Mono',monospace;font-size:9px;letter-spacing:0.28em;text-transform:uppercase;color:#E5D3A3;margin-bottom:8px;">${escapeHtml(m.timeframeLabel)}</div>` : ""}
      <div style="font-family:'Inter','DM Sans',system-ui,sans-serif;font-size:16px;font-weight:500;line-height:1.3;color:#F5F5F5;margin-bottom:8px;">${escapeHtml(m.label)}</div>
      ${m.description ? `<div style="font-size:13px;line-height:1.6;color:rgba(255,255,255,0.6);margin-bottom:${detailLines ? 12 : 0}px;">${escapeHtml(m.description)}</div>` : ""}
      ${detailLines ? `<ul style="list-style:none;padding:0;margin:8px 0 4px;border-top:1px solid rgba(255,255,255,0.08);padding-top:10px;">${detailLines}</ul>` : ""}
      <div style="display:flex;justify-content:space-between;align-items:center;margin-top:12px;padding-top:10px;border-top:1px solid rgba(255,255,255,0.08);font-family:'DM Mono',monospace;font-size:9px;letter-spacing:0.22em;text-transform:uppercase;color:rgba(255,255,255,0.4);">
        <span>${escapeHtml(statusLabel)}</span>
        <span>${m.percent}%</span>
      </div>
    </div>
  `;

  // Position relative to the chart canvas. Chart.js gives caretX/caretY
  // in canvas coords; we add the canvas's offset within the page.
  const { offsetLeft, offsetTop } = chart.canvas;
  const left = offsetLeft + tooltip.caretX;
  const top  = offsetTop + tooltip.caretY;

  // Center the tooltip horizontally above the point, with a small
  // gap. We also clamp to the chart bounds so it never bleeds off.
  const tipW = el.offsetWidth || 280;
  const chartW = chart.canvas.clientWidth;
  let tipLeft = left - tipW / 2;
  if (tipLeft < offsetLeft + 8) tipLeft = offsetLeft + 8;
  if (tipLeft + tipW > offsetLeft + chartW - 8) tipLeft = offsetLeft + chartW - tipW - 8;

  el.style.left = `${tipLeft}px`;
  el.style.top  = `${top - el.offsetHeight - 24}px`;
  el.style.opacity = 1;
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/* ─── Main component ───────────────────────────────────── */
export default function RoadmapChart({ milestones, accent = C.accent, ink = C.ink }) {
  const containerRef  = useRef(null);
  const tooltipElRef  = useRef(null);
  const chartRef      = useRef(null);
  const [width, setWidth] = useState(0);
  const [pulsePos, setPulsePos] = useState(null); // {x, y, r} in px

  // Track container width so we can scale point radius to the chart.
  // ResizeObserver drives this rather than a window listener so we
  // also respond to layout changes (e.g. sidebar collapse).
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setWidth(entry.contentRect.width);
      }
    });
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  // Responsive radius: smaller charts get smaller dots, but always
  // legible. Formula caps growth so very wide screens don't get
  // ridiculously large markers.
  const baseRadius = useMemo(() => {
    if (width < 480) return 8;
    if (width < 720) return 10;
    if (width < 960) return 12;
    return 14;
  }, [width]);
  const hoverRadius = baseRadius + 4;

  // Build the dataset. We use a fixed y value so points lie on a
  // single horizontal line; the line element draws the connector.
  // Chart.js needs a non-zero range, so y = 1 with the y-scale
  // forced to [0, 2] keeps the line visually centered.
  const data = useMemo(() => ({
    labels: milestones.map((m) => m.timeframeLabel || m.label),
    datasets: [{
      data: milestones.map(() => 1),
      // The connecting "axis" line — drawn behind the points.
      borderColor: C.border,
      borderWidth: 1.5,
      tension: 0,
      // Per-point styling
      pointBackgroundColor: milestones.map((m) =>
        isFilled(m) ? colorFor(m, accent, ink) : C.bg,
      ),
      pointBorderColor: milestones.map((m) => colorFor(m, accent, ink)),
      pointBorderWidth: milestones.map((m) => isFilled(m) ? 2 : 2),
      pointRadius: baseRadius,
      pointHoverRadius: hoverRadius,
      pointHoverBackgroundColor: milestones.map((m) => colorFor(m, accent, ink)),
      pointHoverBorderColor: ink,
      pointHoverBorderWidth: 2,
      pointStyle: "circle",
      // Don't fill the area under the line
      fill: false,
    }],
  }), [milestones, accent, ink, baseRadius, hoverRadius]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { top: 20, bottom: 8, left: baseRadius + 8, right: baseRadius + 8 },
    },
    interaction: {
      mode: "nearest",
      intersect: true,
      axis: "x",
    },
    scales: {
      x: {
        display: false,
      },
      y: {
        display: false,
        min: 0,
        max: 2,
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false,
        external: (ctx) => externalTooltipHandler(ctx, milestones, tooltipElRef),
      },
    },
    animation: {
      duration: 400,
    },
  }), [milestones, baseRadius]);

  // After chart renders, locate the in_progress point to position the pulse overlay
  // We poll after a short delay to let Chart.js complete its layout pass
  useEffect(() => {
    if (!chartRef.current) return;
    const inProgressIdx = milestones.findIndex(
      (m) => m.status === "in_progress" && m.percent < 100
    );
    if (inProgressIdx === -1) { setPulsePos(null); return; }

    const update = () => {
      const chart = chartRef.current;
      if (!chart) return;
      const meta = chart.getDatasetMeta(0);
      const pt = meta?.data?.[inProgressIdx];
      if (!pt) return;
      const { x, y } = pt.getProps(["x", "y"], true);
      setPulsePos({ x, y, r: baseRadius });
    };

    // Run after paint so the chart has laid out
    const id = setTimeout(update, 80);
    return () => clearTimeout(id);
  }, [milestones, width, baseRadius]);

  // Height proportional to width but clamped
  const chartHeight = Math.max(160, Math.min(220, width * 0.18));

  return (
    <div style={{ position: "relative" }}>
      {/* The chart */}
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: chartHeight,
          background: "transparent",
          padding: "8px 0",
          position: "relative",
        }}
      >
        <Line ref={chartRef} data={data} options={options} />

        {/* Gold pulse ring — rendered as an absolutely positioned DOM element
            so it sits on top of the canvas without fighting Chart.js */}
        {pulsePos && (
          <>
            {/* Inner ring — tighter, brighter */}
            <div style={{
              position: "absolute",
              left: pulsePos.x,
              top: pulsePos.y,
              width: pulsePos.r * 2,
              height: pulsePos.r * 2,
              borderRadius: "50%",
              background: "rgba(229,211,163,0.45)",
              pointerEvents: "none",
              animation: "goldPulse 2.2s cubic-bezier(0.4,0,0.6,1) infinite",
              transformOrigin: "center",
            }} />
            {/* Outer ring — wider, softer */}
            <div style={{
              position: "absolute",
              left: pulsePos.x,
              top: pulsePos.y,
              width: pulsePos.r * 2,
              height: pulsePos.r * 2,
              borderRadius: "50%",
              background: "rgba(229,211,163,0.18)",
              pointerEvents: "none",
              animation: "goldPulse2 2.2s cubic-bezier(0.4,0,0.6,1) 0.4s infinite",
              transformOrigin: "center",
            }} />
          </>
        )}
      </div>

      {/* External tooltip */}
      <div
        ref={tooltipElRef}
        style={{
          position: "absolute",
          opacity: 0,
          pointerEvents: "none",
          transition: "opacity 0.18s ease, transform 0.18s ease",
          zIndex: 10,
        }}
      />

      {/* Caption row — index + timeframe + label only. No percentages. */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${milestones.length}, 1fr)`,
          gap: 0,
          marginTop: 18,
          padding: `0 ${baseRadius + 8}px`,
        }}
      >
        {milestones.map((m, i) => {
          const active = m.status === "in_progress" && m.percent < 100;
          const done   = m.status === "completed" || m.percent >= 100;
          return (
            <div key={m._id || i} style={{ textAlign: "center", padding: "0 4px" }}>
              <div style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: 9,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: done ? C.ink : active ? C.accent : C.muted,
                marginBottom: 5,
              }}>
                {String(i + 1).padStart(2, "0")}
              </div>
              {m.timeframeLabel && (
                <div style={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 9,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: done ? C.ink : active ? "rgba(229,211,163,0.7)" : C.muted,
                  marginBottom: 5,
                }}>
                  {m.timeframeLabel}
                </div>
              )}
              <div style={{
                fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif",
                fontSize: 12,
                fontWeight: done ? 500 : active ? 400 : 300,
                lineHeight: 1.35,
                color: done ? C.ink : active ? C.accent : C.mid,
              }}>
                {m.label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
